import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbortError } from '../services/apiClient';

export function useCreditorPoll({
  app,
  companyId,
  creditorsListTick,
  setPage,
  bumpCreditorsListTick,
}) {
  const syncTotalPollRef = useRef(null);
  const syncTotalPollAbortRef = useRef(null);
  const [syncQueueActive, setSyncQueueActive] = useState(false);
  const [syncTotalModal, setSyncTotalModal] = useState({
    open: false,
    phase: 'idle',
    pollLogId: null,
  });
  const syncTotalModalRef = useRef(syncTotalModal);
  syncTotalModalRef.current = syncTotalModal;
  const syncQueueActiveRef = useRef(syncQueueActive);
  syncQueueActiveRef.current = syncQueueActive;
  const prevSyncQueueActiveRef = useRef(false);
  const lastCreditorRefreshAtRef = useRef(0);

  const clearSyncTotalPoll = useCallback(() => {
    syncTotalPollAbortRef.current?.abort();
    syncTotalPollAbortRef.current = null;
    if (syncTotalPollRef.current) {
      clearInterval(syncTotalPollRef.current);
      syncTotalPollRef.current = null;
    }
  }, []);

  const closeSyncTotalModal = useCallback(() => {
    clearSyncTotalPoll();
    setSyncTotalModal({ open: false, phase: 'idle', pollLogId: null });
    setPage(1);
    bumpCreditorsListTick();
  }, [bumpCreditorsListTick, clearSyncTotalPoll, setPage]);

  useEffect(() => () => clearSyncTotalPoll(), [clearSyncTotalPoll]);

  useEffect(() => {
    if (!companyId || !app.authToken) {
      setSyncQueueActive(false);
      return undefined;
    }
    let requestAc = null;
    const load = async () => {
      requestAc?.abort();
      requestAc = new AbortController();
      try {
        const st = await app.fetchCompanyCreditorsSyncTotalStatus(companyId, { signal: requestAc.signal });
        const active = Boolean(st?.active);
        const lastLogStatus = String(st?.last_log?.status || '').toLowerCase();
        setSyncQueueActive(active);
        if (active) {
          const now = Date.now();
          const isPending = lastLogStatus === 'pending';
          const minRefreshMs = isPending ? 30000 : 5000;
          if (now - lastCreditorRefreshAtRef.current >= minRefreshMs) {
            bumpCreditorsListTick();
            lastCreditorRefreshAtRef.current = now;
          }
        } else if (prevSyncQueueActiveRef.current) {
          bumpCreditorsListTick();
          lastCreditorRefreshAtRef.current = 0;
        }
        prevSyncQueueActiveRef.current = active;
      } catch (e) {
        if (isAbortError(e)) return;
        setSyncQueueActive(false);
        prevSyncQueueActiveRef.current = false;
        lastCreditorRefreshAtRef.current = 0;
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      clearInterval(id);
      requestAc?.abort();
    };
  }, [companyId, app.authToken, creditorsListTick, bumpCreditorsListTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSyncTotal = useCallback(async () => {
    if (!companyId) return;
    const m = syncTotalModalRef.current;
    if (m.open && (m.phase === 'running' || m.phase === 'polling')) return;
    if (syncQueueActiveRef.current) {
      app.toast('info', 'Sincronização de credores', 'Aguarde atualização de Credores, existem tarefas de Sincronização em andamento.');
      return;
    }
    clearSyncTotalPoll();
    setSyncTotalModal({ open: true, phase: 'running', pollLogId: null });
    try {
      const res = await app.syncCompanyCreditorsTotal(companyId);
      const logId = res?.sync_log?.id;
      if (res?.queued && logId) {
        setSyncTotalModal((prev) => ({ ...prev, phase: 'queued', pollLogId: logId }));
        clearSyncTotalPoll();
        syncTotalPollRef.current = setInterval(async () => {
          syncTotalPollAbortRef.current?.abort();
          const ac = new AbortController();
          syncTotalPollAbortRef.current = ac;
          try {
            const st = await app.fetchCompanyCreditorsSyncTotalStatus(companyId, { signal: ac.signal });
            if (st?.active) return;
            const row = st?.last_log;
            if (!row) return;
            if (row.status !== 'processing' && row.status !== 'running' && row.status !== 'pending') {
              clearSyncTotalPoll();
              if (row.status === 'success') {
                setSyncTotalModal((prev) => ({ ...prev, phase: 'done' }));
                bumpCreditorsListTick();
              } else {
                app.toast('error', 'Sincronização total', row.details || 'Falha na sincronização.');
                setSyncTotalModal({ open: false, phase: 'idle', pollLogId: null });
                app.navigate('company-sync-logs', { companyId });
              }
            }
          } catch (e) {
            if (isAbortError(e)) return;
          }
        }, 2500);
        return;
      }
      setSyncTotalModal((prev) => ({ ...prev, phase: 'done' }));
    } catch (e) {
      clearSyncTotalPoll();
      app.toast('error', 'Sincronização total', e.message || 'Falha ao sincronizar.');
      setSyncTotalModal({ open: false, phase: 'idle', pollLogId: null });
      app.navigate('company-sync-logs', { companyId });
    }
  }, [app, bumpCreditorsListTick, clearSyncTotalPoll, companyId]);

  const syncTotalRunning =
    syncTotalModal.open && (syncTotalModal.phase === 'running' || syncTotalModal.phase === 'queued');

  return {
    syncQueueActive,
    syncTotalModal,
    syncTotalRunning,
    runSyncTotal,
    closeSyncTotalModal,
  };
}
